import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountsDensity, AccountsPlacement } from './AccountsToolbar';

/**
 * EAA-1839 (realm-board parity pass): the "Hiển thị" panel's display
 * preferences — hidden columns, row density and (since 2026-08-24) facet-rail
 * placement — persist across reloads, for whichever accounts board asks. Column
 * WIDTHS already do (the kit's `useResizableColumns`), so the alternative —
 * widths survive, visibility and density do not — is inconsistent with itself.
 *
 * THE ONE COPY, for all three boards. It began as the org board's
 * `Hierarchy/ScopedAccounts/usePersistedViewConfig.ts` and was duplicated here
 * with exactly two changes — the storage key became a PARAMETER instead of a
 * module constant, and `placement` became OPTIONAL, because not every caller
 * offers the choice. That duplicate is gone: this file moved into
 * `Accounts/Shared/` and the org hook was deleted in favour of it (EAA-1839,
 * 2026-08-24), so the realm board, the App Detail accounts tab and the org
 * board now share one implementation.
 *
 * The org board's stored preferences survived that swap untouched: it passes the
 * same `scoped-accounts` key under the same prefix at the same `VERSION`, and
 * the payload shape is identical. Its only visible difference is that an absent
 * or unrecognised `placement` now reads back as `undefined` instead of being
 * coerced to `'sidebar'` here — the caller applies that default itself, which is
 * the point of making the field optional.
 *
 * DO NOT let a second copy reappear: a new board passes a new `tableKey`.
 *
 * The four persistence rules, copied from `useResizableColumns.ts` rather than
 * reinvented:
 *  1. Every storage access is `try/catch`'d — `localStorage` throws in Safari
 *     private mode and does not exist during SSR; a lost preference must never
 *     take the screen down.
 *  2. The payload is versioned. A payload at any other version is discarded
 *     outright, never migrated.
 *  3. A stored `hiddenColumns` entry is filtered against the LIVE column set
 *     on read — a renamed/removed column's stored key is dropped rather than
 *     carried forward forever.
 *  4. Read once on mount; write on change (the reconciliation effects live in
 *     the caller).
 *
 * DELIBERATELY NOT HERE: the active tab, the search term, any filter. Those are
 * per-visit CONTEXT, not a display preference — restoring a stale filter or a
 * leftover search term on a fresh visit would silently show a narrowed list
 * that reads as "there are fewer accounts than there are". Do not widen this
 * hook to cover them.
 *
 * KEY NAMING: `portal:accountsView:` is this app's own prefix, distinct from
 * the kit's `ui:colwidths:` (column WIDTHS) and `DataTable`'s unrelated
 * `ach_columns_`. The suffix must be PER TABLE — the realm board passes
 * `realm-accounts`, the org board `scoped-accounts` — or the two boards
 * overwrite each other's preferences.
 */
const KEY_PREFIX = 'portal:accountsView:';
/**
 * EAA-1839 (2026-08-24): 1 -> 2, because the payload's SHAPE grew a `placement`
 * field (below). Per rule 2 this is a discard, not a migration: every stored v1
 * payload — for `realm-accounts` and for `app-detail-accounts`, the two callers —
 * is dropped once on the next read and reseeded from each caller's own defaults.
 * That is the cheap, correct outcome for three display preferences; do not add a
 * backfill branch here, because "discarded, never migrated" is the contract every
 * reader of this file relies on to know that a stored payload always matches the
 * current interface.
 */
const VERSION = 2;

export interface PersistedAccountsViewConfig {
    hiddenColumns: string[];
    density: AccountsDensity;
    /**
     * EAA-1839 (2026-08-24, user request): where the facet rail renders — the
     * "VỊ TRÍ BỘ LỌC" section of the "Hiển thị" popover. OPTIONAL because not
     * every caller offers the choice; a caller that omits it neither stores nor
     * reads one, and gets `undefined` back rather than a fabricated default, so
     * it can apply its OWN default (the boards default to `'sidebar'`, the App
     * Detail tab to `'header'` — see each hook).
     */
    placement?: AccountsPlacement;
}

/** `'pending'` until the mount-time read resolves; `'empty'` when nothing
 *  usable was stored (first visit, cleared storage, a garbled/old-version
 *  payload) — the caller's own defaults win in both non-config cases. */
export type PersistedAccountsViewLoadState = 'pending' | 'empty' | PersistedAccountsViewConfig;

interface StoredPayload {
    v: number;
    hiddenColumns: unknown;
    density: unknown;
    placement: unknown;
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
}

export function usePersistedAccountsViewConfig(
    tableKey: string,
    liveColumnKeys: readonly string[]
): {
    loadState: PersistedAccountsViewLoadState;
    persist: (next: PersistedAccountsViewConfig) => void;
} {
    const store = useRef(defaultStorage()).current;
    const [loadState, setLoadState] = useState<PersistedAccountsViewLoadState>('pending');

    // Read once on mount. `liveColumnKeys` and `tableKey` are module-level
    // constants at every call site — neither legitimately changes across the
    // component's life, so they are deliberately not dependencies.
    useEffect(() => {
        if (!store) {
            setLoadState('empty');
            return;
        }
        try {
            const raw = store.getItem(KEY_PREFIX + tableKey);
            if (!raw) {
                setLoadState('empty');
                return;
            }
            const parsed = JSON.parse(raw) as StoredPayload;
            if (!parsed || parsed.v !== VERSION) {
                setLoadState('empty');
                return;
            }
            const liveSet = new Set(liveColumnKeys);
            const hiddenColumns = Array.isArray(parsed.hiddenColumns)
                ? parsed.hiddenColumns.filter(
                      (key): key is string => typeof key === 'string' && liveSet.has(key)
                  )
                : [];
            const density: AccountsDensity = parsed.density === 'compact' ? 'compact' : 'comfortable';
            /* Only the two known values are accepted; anything else (including a
               payload written by a caller that does not offer the choice) reads
               back as `undefined` so the caller's own default applies. */
            const placement: AccountsPlacement | undefined =
                parsed.placement === 'sidebar' || parsed.placement === 'header'
                    ? parsed.placement
                    : undefined;
            setLoadState({ hiddenColumns, density, placement });
        } catch {
            // A garbled payload is dropped, not repaired — same as `useResizableColumns`.
            setLoadState('empty');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const persist = useCallback(
        (next: PersistedAccountsViewConfig) => {
            if (!store) return;
            try {
                store.setItem(KEY_PREFIX + tableKey, JSON.stringify({ v: VERSION, ...next }));
            } catch {
                // Silent — see the docstring above.
            }
        },
        [store, tableKey]
    );

    return { loadState, persist };
}
